import { format, formatDistanceToNowStrict } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Fragment } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { AddToChatButton } from '@apps/web/features/admin_agent_dock/add_to_chat_button';
import { useResolveAgentTraceChip } from '@apps/web/features/admin_agent_dock/use_chip_resolvers';
import { cn } from '@apps/web/lib/utils';
import {
    type AgentTraceSortBy,
    type AgentTraceTypeFilterValue,
    CONVERSATION_TYPE_LABELS,
    episodeOutcomeLabel,
    formatEpisodeFilterValue,
    REVIEW_STATUS_LABELS,
    type SortOrder,
    type TRACE_SOURCE_VALUES,
} from '@apps/web/routes/_app/admin/agent-traces/search_params';
import { type components } from '@libraries/typescript/api_client';

type AgentTraceListItem = components['schemas']['AgentTraceListItem'];
type AgentExecutionBackend = components['schemas']['AgentExecutionBackend'];
type ConversationType = components['schemas']['ConversationType'];
type QueryableConversationType = Exclude<ConversationType, 'admin_playground'>;
type TraceSourceFilter = (typeof TRACE_SOURCE_VALUES)[number];
type AgentTraceReviewStatus = AgentTraceListItem['review_status'];
type AgentTraceSourceBadge = {
    readonly label: string;
    readonly source: TraceSourceFilter;
};
type AgentTraceSourceBadgeFields = Pick<
    AgentTraceListItem,
    | 'slack_app_variant'
    | 'source'
    | 'is_admin_run'
    | 'is_training_mode_run'
    | 'is_agent_v3'
    | 'is_agent_v3_internal'
>;

const MAX_TRACE_LABEL_BADGES = 3;
const REVIEW_STATUS_BADGE_CLASS_NAMES: Record<AgentTraceReviewStatus, string> =
    {
        needs_review:
            'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
        ticket_filed:
            'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
        false_positive: 'bg-muted text-foreground/80',
        known_issue:
            'bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300',
        reviewed:
            'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    };

function isQueryableConversationType(
    conversationType: ConversationType,
): conversationType is QueryableConversationType {
    return conversationType !== 'admin_playground';
}

export function getAgentTraceTypeLabel(
    trace: Pick<AgentTraceListItem, 'conversation_type' | 'run_type'>,
): string {
    if (trace.run_type === 'automation') {
        return 'Automation';
    }
    if (!isQueryableConversationType(trace.conversation_type)) {
        return 'Admin Playground';
    }
    return CONVERSATION_TYPE_LABELS[trace.conversation_type];
}

export function getAgentTraceSlackSourceLabel(
    trace: Pick<AgentTraceListItem, 'slack_app_variant'>,
): string | null {
    if (!trace.slack_app_variant) {
        return null;
    }
    return trace.slack_app_variant === 'v2' ? 'Slack v2' : 'Slack';
}

export function getAgentTraceSourceLabel(
    trace: AgentTraceSourceBadgeFields,
): string | null {
    return getAgentTraceSourceBadges(trace)[0]?.label ?? null;
}

export function getAgentTraceSourceBadges(
    trace: AgentTraceSourceBadgeFields,
): Array<AgentTraceSourceBadge> {
    const badges: Array<AgentTraceSourceBadge> = [];
    const slackLabel = getAgentTraceSlackSourceLabel(trace);
    if (slackLabel !== null) {
        badges.push({ label: slackLabel, source: 'slack' });
    }
    if (trace.is_admin_run) {
        badges.push({ label: 'Admin', source: 'admin' });
    }
    if (trace.is_training_mode_run) {
        badges.push({ label: 'Training', source: 'training' });
    }
    if (trace.is_agent_v3) {
        badges.push({ label: 'Agent v3', source: 'v3' });
    }
    if (trace.is_agent_v3_internal) {
        badges.push({ label: 'Internal read-only', source: 'v3' });
    }
    if (trace.source === 'linq_imessage') {
        badges.push({ label: 'iMessage', source: 'linq_imessage' });
    }
    if (trace.source === 'linq_imessage_subagent') {
        badges.push({
            label: 'iMessage subagent',
            source: 'linq_imessage_subagent',
        });
    }
    if (trace.source === 'extension') {
        badges.push({ label: 'Extension', source: 'extension' });
    }
    return badges;
}

export function getAgentTraceExecutionBackendLabel(
    executionBackend: AgentExecutionBackend,
): string | null {
    if (executionBackend === 'event') {
        return null;
    }
    if (executionBackend === 'temporal') {
        return 'Temporal';
    }
    return 'Legacy';
}

function formatCostUsd(costUsd: AgentTraceListItem['total_cost_usd']): string {
    if (costUsd === null || costUsd === undefined) {
        return '—';
    }
    return `$${Number(costUsd).toFixed(4)}`;
}

function agentTraceCostTitle(trace: AgentTraceListItem): string {
    if (!trace.cost_complete) {
        return 'All-in cost unavailable: some child or swarm usage is historical or unpriced.';
    }
    return [
        `Own ${formatCostUsd(trace.own_cost_usd)}`,
        `subagents ${formatCostUsd(trace.subagent_cost_usd)}`,
        `swarm ${formatCostUsd(trace.swarm_cost_usd)}`,
    ].join(' · ');
}

function formatDurationMs(
    durationMs: AgentTraceListItem['duration_ms'],
): string {
    if (durationMs === null || durationMs === undefined) {
        return '—';
    }
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
}

function getReviewStatusBadgeClassName(status: AgentTraceReviewStatus): string {
    return REVIEW_STATUS_BADGE_CLASS_NAMES[status];
}

type AgentTraceEpisodeTag = {
    readonly use_case?: string | null;
    readonly outcome?: string | null;
    readonly agent_behavior?: string | null;
};

export function episodeTagLabel(tag: AgentTraceEpisodeTag): string {
    const useCase = tag.use_case
        ? formatEpisodeFilterValue(tag.use_case)
        : 'unclassified';
    return tag.outcome
        ? `${useCase} · ${episodeOutcomeLabel(tag.outcome)}`
        : useCase;
}

const FAILURE_EPISODE_OUTCOMES: ReadonlySet<string> = new Set([
    'declined_unsupported',
    'false_success',
    'gave_up',
    'unresolved',
]);

// Episode-judge chips: green for clean completions, red when the judge saw
// a failure-shaped outcome or behavior, neutral otherwise.
export function getEpisodeTagBadgeClassName(tag: AgentTraceEpisodeTag): string {
    if (
        tag.agent_behavior === 'failure' ||
        (typeof tag.outcome === 'string' &&
            FAILURE_EPISODE_OUTCOMES.has(tag.outcome))
    ) {
        return 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300';
    }
    if (tag.outcome === 'done') {
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300';
    }
    return '';
}

export function AgentTraceTable({
    traces,
    onSelectTrace,
    buildTraceHref,
    sortBy,
    sortOrder,
    onSort,
    onFilterConversationType,
    onFilterTraceSource,
    selectedTraceIds,
    onToggleTraceSelected,
    onToggleSelectAll,
}: {
    readonly traces: Array<AgentTraceListItem>;
    readonly onSelectTrace: (traceId: string) => void;
    readonly buildTraceHref: (traceId: string) => string;
    readonly sortBy: AgentTraceSortBy;
    readonly sortOrder: SortOrder;
    readonly onSort: (sortBy: AgentTraceSortBy) => void;
    readonly onFilterConversationType: (
        type: AgentTraceTypeFilterValue,
    ) => void;
    readonly onFilterTraceSource: (source: TraceSourceFilter) => void;
    readonly selectedTraceIds: ReadonlySet<string>;
    readonly onToggleTraceSelected: (traceId: string) => void;
    readonly onToggleSelectAll: () => void;
}) {
    const resolveAgentTraceChip = useResolveAgentTraceChip();
    const allSelected =
        traces.length > 0 &&
        traces.every((trace) => selectedTraceIds.has(trace.id));
    const someSelected = traces.some((trace) => selectedTraceIds.has(trace.id));
    const selectAllState = allSelected
        ? true
        : someSelected
          ? 'indeterminate'
          : false;
    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-10">
                            <Checkbox
                                checked={selectAllState}
                                onCheckedChange={onToggleSelectAll}
                                aria-label="Select all traces"
                            />
                        </TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Meta</TableHead>
                        <TableHead className="text-right">
                            <SortableHeader
                                label="All-in cost"
                                sortKey="total_cost_usd"
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={onSort}
                            />
                        </TableHead>
                        <TableHead className="text-right">
                            <SortableHeader
                                label="Time spent"
                                sortKey="duration_ms"
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={onSort}
                            />
                        </TableHead>
                        <TableHead className="text-right">
                            <SortableHeader
                                label="First gen"
                                sortKey="first_response_ready_latency_ms"
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={onSort}
                            />
                        </TableHead>
                        <TableHead>
                            <SortableHeader
                                label="Created"
                                sortKey="created_at"
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={onSort}
                            />
                        </TableHead>
                        <TableHead>Last Activity</TableHead>
                        <TableHead className="w-10">
                            <span className="sr-only">Add to agent chat</span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {traces.map((trace) => {
                        const href = buildTraceHref(trace.id);
                        const sourceBadges = getAgentTraceSourceBadges(trace);
                        const executionBackendLabel =
                            getAgentTraceExecutionBackendLabel(
                                trace.execution_backend,
                            );
                        const organizationInternalName =
                            trace.organization_internal_name ?? null;
                        const isSelected = selectedTraceIds.has(trace.id);
                        return (
                            <TableRow
                                key={trace.id}
                                data-state={isSelected ? 'selected' : undefined}
                                className="group/trace-row hover:bg-muted/50 cursor-pointer"
                                onClick={(event) => {
                                    if (
                                        event.metaKey ||
                                        event.ctrlKey ||
                                        event.shiftKey
                                    ) {
                                        window.open(
                                            href,
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                        return;
                                    }
                                    onSelectTrace(trace.id);
                                }}
                                onAuxClick={(event) => {
                                    if (event.button === 1) {
                                        window.open(
                                            href,
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                    }
                                }}
                            >
                                <TableCell
                                    className="w-10"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() =>
                                            onToggleTraceSelected(trace.id)
                                        }
                                        aria-label={`Select trace ${trace.id.slice(
                                            0,
                                            8,
                                        )}`}
                                    />
                                </TableCell>
                                <TableCell className="max-w-[180px] text-sm">
                                    <div className="min-w-0">
                                        <div className="truncate">
                                            {trace.organization_name ?? '—'}
                                        </div>
                                        {organizationInternalName ? (
                                            <div
                                                className="text-muted-foreground mt-0.5 truncate text-xs"
                                                title={organizationInternalName}
                                            >
                                                {organizationInternalName}
                                            </div>
                                        ) : null}
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm whitespace-nowrap">
                                    <Badge
                                        variant="outline"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (
                                                trace.run_type === 'automation'
                                            ) {
                                                onFilterConversationType(
                                                    'automation',
                                                );
                                                return;
                                            }
                                            if (
                                                !isQueryableConversationType(
                                                    trace.conversation_type,
                                                )
                                            ) {
                                                return;
                                            }
                                            onFilterConversationType(
                                                trace.conversation_type,
                                            );
                                        }}
                                    >
                                        {getAgentTraceTypeLabel(trace)}
                                    </Badge>
                                </TableCell>
                                <TableCell className="max-w-[160px] text-sm">
                                    {trace.owner_user_name ? (
                                        <span className="truncate">
                                            {trace.owner_user_name}
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            Unassigned
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-sm">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <Badge
                                            variant="outline"
                                            className="font-mono text-xs"
                                        >
                                            {trace.id.slice(0, 8)}
                                        </Badge>
                                        <Badge
                                            variant="secondary"
                                            className={cn(
                                                'text-xs',
                                                getReviewStatusBadgeClassName(
                                                    trace.review_status,
                                                ),
                                            )}
                                        >
                                            {
                                                REVIEW_STATUS_LABELS[
                                                    trace.review_status
                                                ]
                                            }
                                        </Badge>
                                        {(trace.linked_linear_issues ?? []).map(
                                            (issue) => (
                                                <a
                                                    key={issue.identifier}
                                                    href={issue.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                    className="inline-flex"
                                                >
                                                    <Badge
                                                        variant="outline"
                                                        className="text-xs"
                                                    >
                                                        {issue.identifier}
                                                    </Badge>
                                                </a>
                                            ),
                                        )}
                                        {trace.model ? (
                                            <Badge
                                                variant="outline"
                                                className="max-w-[160px] font-mono text-xs"
                                                title={trace.model}
                                            >
                                                <span className="truncate">
                                                    {trace.model}
                                                </span>
                                            </Badge>
                                        ) : null}
                                        {executionBackendLabel ? (
                                            <Badge
                                                variant="secondary"
                                                className="bg-blue-100 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300"
                                            >
                                                {executionBackendLabel}
                                            </Badge>
                                        ) : null}
                                        {sourceBadges.map((sourceBadge) => (
                                            <Badge
                                                key={sourceBadge.label}
                                                variant="secondary"
                                                className="bg-emerald-100 text-xs text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onFilterTraceSource(
                                                        sourceBadge.source,
                                                    );
                                                }}
                                            >
                                                {sourceBadge.label}
                                            </Badge>
                                        ))}
                                        {trace.conversation_type ===
                                        'outreach_analysis' ? (
                                            <Fragment>
                                                {trace.urgent_outreach ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="bg-rose-100 text-xs text-rose-800 dark:bg-rose-950/50 dark:text-rose-300"
                                                    >
                                                        Urgent outreach
                                                    </Badge>
                                                ) : null}
                                                {trace.outreach_status ? (
                                                    <OutreachStatusBadge
                                                        status={
                                                            trace.outreach_status
                                                        }
                                                    />
                                                ) : null}
                                                {trace.suggested_caregiver_count !==
                                                null ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="text-xs"
                                                    >
                                                        {
                                                            trace.suggested_caregiver_count
                                                        }{' '}
                                                        suggested
                                                    </Badge>
                                                ) : null}
                                                {trace.auto_drafted === true ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="text-xs"
                                                    >
                                                        Auto-draft
                                                    </Badge>
                                                ) : null}
                                            </Fragment>
                                        ) : null}
                                        {trace.has_llm_judge_note ? (
                                            <Badge
                                                variant="secondary"
                                                className="bg-violet-100 text-xs text-violet-800 dark:bg-violet-950/50 dark:text-violet-300"
                                            >
                                                LLM Judge
                                            </Badge>
                                        ) : null}
                                        {(trace.episode_tags ?? []).map(
                                            (tag, index) => (
                                                <Badge
                                                    key={`${tag.use_case}:${tag.outcome}:${index}`}
                                                    variant="secondary"
                                                    className={cn(
                                                        'text-xs',
                                                        getEpisodeTagBadgeClassName(
                                                            tag,
                                                        ),
                                                    )}
                                                >
                                                    {episodeTagLabel(tag)}
                                                </Badge>
                                            ),
                                        )}
                                        {trace.note_count > 0 ? (
                                            <Badge
                                                variant="secondary"
                                                className="text-xs"
                                            >
                                                {trace.note_count}{' '}
                                                {trace.note_count === 1
                                                    ? 'note'
                                                    : 'notes'}
                                            </Badge>
                                        ) : null}
                                        {trace.annotation_labels
                                            .slice(0, MAX_TRACE_LABEL_BADGES)
                                            .map((label) => (
                                                <Badge
                                                    key={label}
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {label}
                                                </Badge>
                                            ))}
                                        {trace.annotation_labels.length >
                                        MAX_TRACE_LABEL_BADGES ? (
                                            <Badge
                                                variant="secondary"
                                                className="text-xs"
                                            >
                                                +
                                                {trace.annotation_labels
                                                    .length -
                                                    MAX_TRACE_LABEL_BADGES}
                                            </Badge>
                                        ) : null}
                                    </div>
                                </TableCell>
                                <TableCell
                                    className="text-right text-sm whitespace-nowrap"
                                    title={agentTraceCostTitle(trace)}
                                >
                                    {formatCostUsd(trace.total_cost_usd)}
                                </TableCell>
                                <TableCell className="text-right text-sm whitespace-nowrap">
                                    {formatDurationMs(trace.duration_ms)}
                                </TableCell>
                                <TableCell className="text-right text-sm whitespace-nowrap">
                                    {formatDurationMs(
                                        trace.first_response_ready_latency_ms,
                                    )}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                    {format(
                                        new Date(trace.created_at),
                                        'MMM d, h:mm a',
                                    )}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                    {trace.last_turn_at
                                        ? formatDistanceToNowStrict(
                                              new Date(trace.last_turn_at),
                                              { addSuffix: true },
                                          )
                                        : '—'}
                                </TableCell>
                                <TableCell
                                    className="w-10 pr-2"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <AddToChatButton
                                        kind="agent_trace"
                                        entityId={trace.id}
                                        resolveChip={() =>
                                            resolveAgentTraceChip({
                                                conversationId: trace.id,
                                                createdAt: trace.created_at,
                                            })
                                        }
                                        className="opacity-0 group-hover/trace-row:opacity-100 data-[add-state=added]:opacity-100 data-[add-state=duplicate]:opacity-100 data-[add-state=pending]:opacity-100"
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

function SortableHeader({
    label,
    sortKey,
    sortBy,
    sortOrder,
    onSort,
}: {
    readonly label: string;
    readonly sortKey: AgentTraceSortBy;
    readonly sortBy: AgentTraceSortBy;
    readonly sortOrder: SortOrder;
    readonly onSort: (sortBy: AgentTraceSortBy) => void;
}) {
    const isActive = sortBy === sortKey;
    const Icon = isActive
        ? sortOrder === 'asc'
            ? ArrowUp
            : ArrowDown
        : ArrowUpDown;

    return (
        <Button
            variant="ghost"
            size="sm"
            className={cn('h-6 px-1 text-xs', isActive && 'text-foreground')}
            onClick={() => onSort(sortKey)}
        >
            <Icon className="mr-1 h-3 w-3" />
            {label}
        </Button>
    );
}

const OUTREACH_STATUS_STYLES: Record<string, string> = {
    filled: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    in_progress:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
    unfilled: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    pending: 'bg-muted text-foreground/80',
    paused: 'bg-muted text-foreground/80',
};

function OutreachStatusBadge({ status }: { readonly status: string }) {
    return (
        <Badge
            variant="secondary"
            className={cn('text-xs', OUTREACH_STATUS_STYLES[status])}
        >
            {status.replace('_', ' ')}
        </Badge>
    );
}
